import { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';
import {
  ChevronRight,
  ChevronDown,
  FileText,
  MoreHorizontal,
  Plus,
  Trash2,
  GripVertical,
} from 'lucide-react';

// Single sortable tree item - flattened (no recursion)
const DocumentTreeItem = ({
  item,
  level,
  selectedId,
  onSelect,
  onDelete,
  onCreateChild,
  isExpanded,
  hasChildren,
  onToggleExpand,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isSelected = selectedId === item.id;

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className={cn(
          'group flex items-center gap-1 px-2 py-1.5 rounded-md text-sm cursor-pointer transition-colors',
          isSelected
            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
        )}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        data-testid={`doc-tree-item-${item.id}`}
      >
        {/* Drag Handle */}
        <button
          className="opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing p-0.5 -ml-1"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-3 h-3 text-slate-400" />
        </button>

        {/* Expand/Collapse */}
        <button
          className={cn(
            'p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700',
            !hasChildren && 'invisible'
          )}
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand(item.id);
          }}
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>

        {/* Document Icon & Title */}
        <div
          className="flex-1 flex items-center gap-2 min-w-0"
          onClick={() => onSelect(item.id)}
        >
          <FileText className="w-4 h-4 flex-shrink-0 text-slate-400" />
          <span className="truncate">{item.title}</span>
        </div>

        {/* Actions Menu */}
        {(onDelete || onCreateChild) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 opacity-0 group-hover:opacity-100"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onCreateChild && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateChild(item.id);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add sub-document
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(item.id);
                  }}
                  className="text-red-600 dark:text-red-400"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};

// Main DocumentTree component
const DocumentTree = ({
  documents,
  selectedId,
  onSelect,
  onDelete,
  onCreateChild,
  onReorder,
  autoExpandId,
  onAutoExpandHandled,
  onAutoExpandComplete,
}) => {
  const onExpandDone = onAutoExpandHandled || onAutoExpandComplete;
  const [expandedIds, setExpandedIds] = useState(new Set());

  // Auto-expand parent when creating a sub-document
  useEffect(() => {
    if (autoExpandId) {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.add(autoExpandId);
        return next;
      });
      if (onExpandDone) {
        onExpandDone();
      }
    }
  }, [autoExpandId, onExpandDone]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Flatten tree into a list for rendering (iterative, no recursion)
  const flattenedItems = [];
  const stack = documents.map((doc, idx) => ({ item: doc, level: 0, index: idx })).reverse();
  
  while (stack.length > 0) {
    const { item, level } = stack.pop();
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedIds.has(item.id);
    
    flattenedItems.push({
      ...item,
      level,
      hasChildren,
      isExpanded,
    });
    
    if (hasChildren && isExpanded) {
      // Add children in reverse order so they come out in correct order
      for (let i = item.children.length - 1; i >= 0; i--) {
        stack.push({ item: item.children[i], level: level + 1, index: i });
      }
    }
  }

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id || !onReorder) return;

    // Find items in flattened list
    const activeIdx = flattenedItems.findIndex((i) => i.id === active.id);
    const overIdx = flattenedItems.findIndex((i) => i.id === over.id);
    
    if (activeIdx === -1 || overIdx === -1) return;

    // For root level items only (same level reordering)
    const activeItem = flattenedItems[activeIdx];
    const overItem = flattenedItems[overIdx];
    
    if (activeItem.level === 0 && overItem.level === 0) {
      const rootItems = flattenedItems.filter((i) => i.level === 0);
      const oldIndex = rootItems.findIndex((i) => i.id === active.id);
      const newIndex = rootItems.findIndex((i) => i.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(rootItems, oldIndex, newIndex);
        onReorder(newOrder.map((d) => d.id), null);
      }
    }
  };

  if (!documents || documents.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-slate-500 dark:text-slate-500">
        No documents yet
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={flattenedItems.map((i) => i.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-0.5" data-testid="document-tree">
          {flattenedItems.map((item) => (
            <DocumentTreeItem
              key={item.id}
              item={item}
              level={item.level}
              selectedId={selectedId}
              onSelect={onSelect}
              onDelete={onDelete}
              onCreateChild={onCreateChild}
              isExpanded={item.isExpanded}
              hasChildren={item.hasChildren}
              onToggleExpand={toggleExpand}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default DocumentTree;
